import React from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../styles/theme';

interface CardProps {
  children: React.ReactNode;
  variant?: 'default' | 'elevated' | 'outlined';
  padding?: 'small' | 'medium' | 'large';
  onClick?: () => void;
  className?: string;
}

const StyledCard = styled.div.withConfig({
  shouldForwardProp: (prop) => !['variant', 'padding'].includes(prop),
})<CardProps>`
  background: ${theme.gradients.card};
  border-radius: ${theme.borderRadius.large};
  transition: all ${theme.transitions.normal};
  position: relative;
  overflow: hidden;
  
  ${({ variant }) => {
    switch (variant) {
      case 'elevated':
        return css`
          box-shadow: ${theme.shadows.large};
          
          &:hover {
            transform: translateY(-4px);
            box-shadow: ${theme.shadows.large}, ${theme.shadows.glow};
          }
        `;
      case 'outlined':
        return css`
          border: 1px solid ${theme.colors.border};
          
          &:hover {
            border-color: ${theme.colors.primary};
          }
        `;
      default:
        return css`
          box-shadow: ${theme.shadows.medium};
          
          &:hover {
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.large};
          }
        `;
    }
  }}
  
  ${({ padding }) => {
    switch (padding) {
      case 'small':
        return css`
          padding: ${theme.spacing.md};
        `;
      case 'large':
        return css`
          padding: ${theme.spacing.xl};
        `;
      default:
        return css`
          padding: ${theme.spacing.lg};
        `;
    }
  }}
  
  ${({ onClick }) => onClick && css`
    cursor: pointer;
  `}
`;

export const Card: React.FC<CardProps> = ({
  children,
  variant = 'default',
  padding = 'medium',
  onClick,
  className
}) => {
  return (
    <StyledCard
      variant={variant}
      padding={padding}
      onClick={onClick}
      className={className}
    >
      {children}
    </StyledCard>
  );
}; 